import React from 'react'
import { useDispatch } from 'react-redux'
import { compose } from '@cbgms/base/utils/compose'
import { useNotifier } from '@cbgms/base/notifier'
import { TooltipButton } from '@cbgms/components/form/TooltipButton'
import { WithSaveConfirmationModalOuterProps, withSaveConfirmationModal } from '@cbgms/components/layout/WithSaveConfirmationModal'
import {
  copyProductGroup,
  createProductGroup,
  deleteProductGroup,
  getAllProductTrees,
  importProductGroup
} from 'app-support/productgroup/state/actions'
import SupportListWrapper from 'core/page/SupportListWrapper'
import { Locales } from '@carsys/enums/locales'
import UploadIcon from '@mui/icons-material/Upload'
import { useTranslation } from 'react-i18next'
import { IProductGroupResponse } from '@cbgms/api'
import { ListRow } from 'core/types/ListRow'
import { useHistory } from 'react-router-dom'
import { Fab } from '@mui/material'

type ProductGroupListProps = WithSaveConfirmationModalOuterProps

const enhance = compose<ProductGroupListProps, {}>(
  withSaveConfirmationModal({
    showDialogOnSave: true,
    dialogTitle: 'productgroup.copy_title',
    dialogText: 'productgroup.copy_text'
  })
)

function useProductListHandlers(handleConfirmSubmit: WithSaveConfirmationModalOuterProps['handleConfirmSubmit']) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const notifier = useNotifier()
  const history = useHistory()

  const onEditHandler = React.useCallback((row: IProductGroupResponse) => {
    history.push(`/product-group/${row.ProductGroup.UUID}`)
  }, [])

  const onDeleteHandler = React.useCallback(async (row: IProductGroupResponse, dryRun: boolean) => {
    const result = await dispatch(deleteProductGroup(row.ProductGroup.UUID, dryRun))

    if (!dryRun) {
      notifier.success(t('productgroup.deleted_successfully'))
    }

    return result
  }, [])

  const onCopyHandler = React.useCallback(async (row: ListRow<IProductGroupResponse>) => {
    try {
      const { Data } = await dispatch(copyProductGroup(row.data.ProductGroup.UUID))

      history.push(`/product-group/${Data.UUID}`)
    } catch {
      // Do nothing
    }
  }, [])

  const actions = React.useCallback(
    (row: ListRow<IProductGroupResponse>) => (
      <TooltipButton title={t('global.copy')} icon='copy' onClick={handleConfirmSubmit(() => onCopyHandler(row))} />
    ),
    [handleConfirmSubmit]
  )

  return React.useMemo(
    () => ({
      actions,
      onEdit: onEditHandler,
      onDelete: onDeleteHandler
    }),
    [actions]
  )
}

export const ProductGroupList: React.FC<ProductGroupListProps> = props => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()
  const notifier = useNotifier()
  const { actions, onEdit, onDelete } = useProductListHandlers(props.handleConfirmSubmit)

  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const onImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files

    if (!files || files.length !== 1) {
      return notifier.error(t('ProductGroup.NoFiles'))
    }

    const file = files[0]

    if (!['text/plain', 'application/json'].includes(file.type)) {
      return notifier.error(t('ProductGroup.InvalidFileType'))
    }

    try {
      const { Data } = await dispatch(importProductGroup(file))

      history.push(`/product-group/${Data.UUID}`)

      notifier.success(t('ProductGroup.ImportSuccess'))
    } catch {
      notifier.error(t('ProductGroup.ImportFailed'))
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const onAdd = React.useCallback(async () => {
    const { Data } = await dispatch(
      createProductGroup({
        Name: 'NEW TREE',
        Locale: Locales.en_GB
      })
    )

    history.push(`/product-group/${Data.UUID}`)
  }, [])

  return (
    <React.Fragment>
      <input type='file' data-testid='productgroup-list-importfile' style={{ display: 'none' }} onChange={onImport} ref={fileInputRef} />
      <SupportListWrapper
        scope='product-group-tree'
        hideExport
        translationKey='productgroup'
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
        actions={actions}
        loadAction={getAllProductTrees}
        menuItems={
          <Fab
            title={t('ProductGroup.ImportTree')}
            color='primary'
            size='small'
            data-testid='productgroup-list-import'
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <UploadIcon />
          </Fab>
        }
        {...props}
      />
    </React.Fragment>
  )
}

export default enhance(ProductGroupList)
